import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lightning, ArrowRight } from "@phosphor-icons/react";

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signup(form.name, form.email, form.password);
      toast.success("Workspace created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 mb-3">/// signup</div>
          <h1 className="font-heading text-4xl tracking-tight mb-8">Create your workspace.</h1>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Your name</label>
              <input
                data-testid="signup-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-3 outline-none transition-colors"
                placeholder="Jane Doe"
              />
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Email</label>
              <input
                data-testid="signup-email-input"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-3 outline-none transition-colors"
                placeholder="you@company.com"
              />
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-widest text-white/40 font-mono">Password</label>
              <input
                data-testid="signup-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                minLength={6}
                className="w-full mt-1 bg-transparent border border-white/10 focus:border-white/40 rounded-sm px-3 py-3 outline-none transition-colors"
                placeholder="min 6 characters"
              />
            </div>

            <button
              data-testid="signup-submit-button"
              type="submit"
              disabled={busy}
              className="w-full bg-white text-black rounded-sm px-4 py-3 font-medium flex items-center justify-center gap-2 hover:bg-white/90 transition-colors disabled:opacity-50"
            >
              {busy ? "Creating workspace…" : "Create workspace"}
              <ArrowRight size={16} weight="bold" />
            </button>
          </form>

          <div className="mt-8 text-sm text-white/60">
            Already have an account?{" "}
            <Link data-testid="signup-goto-login" to="/login" className="text-white underline underline-offset-4 hover:text-white/80 transition-colors">
              Sign in
            </Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex flex-col justify-between p-12 border-l border-white/10 order-1 lg:order-2 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{
          backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }} />
        <div className="flex items-center gap-2 relative">
          <div className="h-9 w-9 rounded-sm bg-white flex items-center justify-center">
            <Lightning size={20} weight="fill" className="text-black" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono">Follow-up OS</div>
            <div className="font-heading text-xl leading-none tracking-tight">Omni Reach</div>
          </div>
        </div>

        <div className="relative space-y-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">/// what you get</div>
          <ul className="space-y-4">
            {[
              ["Unified inbox", "See every DM from WhatsApp and Instagram in one thread view."],
              ["Cadences per channel", "Rules for new followers, story replies, likes and manual leads."],
              ["OAuth Meta", "Connect Instagram Business and WhatsApp Cloud with signed webhooks."],
              ["Team roles", "Owner, admin, member — bring your sales team along."],
            ].map(([h, d]) => (
              <li key={h} className="border-l-2 border-white/10 pl-4">
                <div className="text-sm font-medium">{h}</div>
                <div className="text-sm text-white/50">{d}</div>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative font-mono text-[11px] uppercase tracking-[0.25em] text-white/40">
          v0.1.0 — beta
        </div>
      </div>
    </div>
  );
}
